/**
 * OsintEgressNotice — the "you are about to go online" surface for the OSINT
 * agent.
 *
 * Spectre is otherwise on-prem and air-gappable; this one feature reaches the
 * live internet, so the transition deserves to be visible rather than implied.
 * Two parts, both rendered by the OSINT index page:
 *
 * - `OsintEgressCard` — always on, above the composer. Ambient copy cannot be
 *   dismissed and forgotten.
 * - `OsintEgressDialog` — a one-time gate before the first run in a case,
 *   acknowledged into localStorage. There is no server-side preference store in
 *   this app, so the acknowledgement is per-browser by construction.
 *
 * On the copy: the protections listed are the TARGET architecture (ADR-0007's
 * single egress), not what ships today — outbound calls currently leave over
 * plain TLS from the deployment's own address. The strings are therefore worded
 * as planned protections and the card states what holds right now, so nobody
 * reads the panel as a promise of anonymity they don't have.
 */

import { ShieldAlert } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Card } from '@/components/ui/card'

const ACK_KEY_PREFIX = 'spectre:osint:egress-ack:'

function ackKey(caseId: string): string {
  return `${ACK_KEY_PREFIX}${caseId}`
}

function readAck(caseId: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(ackKey(caseId)) === 'true'
  } catch {
    return false
  }
}

function writeAck(caseId: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ackKey(caseId), 'true')
  } catch {
    /* a locked-down browser just means the gate shows again */
  }
}

/** Persistent notice above the composer. */
export function OsintEgressCard() {
  const { t } = useTranslation()
  return (
    <Card size="compact" tone="warning" className="flex items-start gap-3 p-4">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-warning/10 text-warning">
        <ShieldAlert className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold text-foreground">{t('osint.egress.title')}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{t('osint.egress.body')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('osint.egress.planned')}</p>
      </div>
    </Card>
  )
}

/**
 * Gate the first run in a case behind an explicit acknowledgement.
 *
 * Returns `guard`, which wraps the start action: if the case has not been
 * acknowledged it opens the dialog and defers the action until confirmation,
 * otherwise it runs straight through.
 */
export function useOsintEgressGate(caseId: string) {
  const [acknowledged, setAcknowledged] = useState(() => readAck(caseId))
  const [pending, setPending] = useState<(() => void) | null>(null)

  const guard = useCallback(
    (action: () => void) => {
      if (acknowledged) {
        action()
        return
      }
      setPending(() => action)
    },
    [acknowledged]
  )

  const confirm = useCallback(() => {
    writeAck(caseId)
    setAcknowledged(true)
    pending?.()
    setPending(null)
  }, [caseId, pending])

  const cancel = useCallback(() => setPending(null), [])

  return { guard, open: pending !== null, confirm, cancel }
}

interface DialogProps {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
}

/** One-time confirmation shown before the first OSINT run in a case. */
export function OsintEgressDialog({ open, onConfirm, onCancel }: DialogProps) {
  const { t } = useTranslation()
  return (
    <AlertDialog open={open} onOpenChange={next => !next && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('osint.egress.dialogTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('osint.egress.dialogBody')}</AlertDialogDescription>
        </AlertDialogHeader>
        <p className="text-xs text-muted-foreground">{t('osint.egress.planned')}</p>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{t('osint.egress.confirm')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
